import {
  JOB_FIGURE_LABELS,
  JOB_STATE_LABELS,
  type JobFigure,
  type JobKind,
  type JobPerson,
  type JobState,
} from '@k-management/shared';
import { Icon, type IconName } from '../../components/Icon.js';

export const jobKeys = {
  all: ['jobs'] as const,
  list: (kind: JobKind) => ['jobs', 'list', kind] as const,
  client: (clientId: string) => ['jobs', 'client', clientId] as const,
  job: (id: string) => ['jobs', 'job', id] as const,
  history: (id: string) => ['jobs', 'job', id, 'history'] as const,
  pending: ['jobs', 'pending-offers'] as const,
  options: ['jobs', 'options'] as const,
};

/** Where a commessa or a progetto lives in the app. */
export function jobPath(job: { id: string; kind: JobKind }): string {
  return job.kind === 'commessa' ? `/commesse/${job.id}` : `/progetti/${job.id}`;
}

const STATE_CLASS: Record<JobState, string> = {
  in_preparazione: 'warn',
  in_corso: 'ok',
  sospesa: 'info',
  chiusa: 'muted',
  annullata: 'bad',
};

export function JobStatePill({ state }: { state: JobState }) {
  return <span className={`pill ${STATE_CLASS[state]}`}>{JOB_STATE_LABELS[state]}</span>;
}

/** «Paolo Monti» or «Paolo Monti, Laura Neri»; «—» without anyone. */
export function namesOf(people: readonly JobPerson[], figure: JobFigure): string {
  const names = people.filter((person) => person.figure === figure).map((person) => person.name);
  return names.length ? names.join(', ') : '—';
}

export const FIGURE_ORDER: readonly JobFigure[] = ['pm', 'account', 'commerciale'];

export function figureLabel(figure: JobFigure): string {
  return JOB_FIGURE_LABELS[figure];
}

/** A bar from 0 to 1: the budget spent, the way of an indicator (2.41). */
export function Meter({ share, over = false }: { share: number; over?: boolean }) {
  const percent = Math.round(Math.min(1, Math.max(0, share)) * 100);
  return (
    <span className={`meter${over ? ' over' : ''}`} role="img" aria-label={`${percent}%`}>
      <span style={{ width: `${percent}%` }} />
    </span>
  );
}

/** Whether a progetto spent more than its budget of the costs. */
export function overBudget(costs: { budget: string; actual: string } | null): boolean {
  return costs !== null && Number(costs.actual) > Number(costs.budget);
}

/** An action of a row of the cicli, as an icon with its description on hover (2.55). */
export function RowAction({
  icon,
  label,
  onClick,
  disabled = false,
}: {
  icon: IconName;
  label: string;
  onClick: () => void;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      className="icon-button"
      title={label}
      aria-label={label}
      disabled={disabled}
      onClick={onClick}
    >
      <Icon name={icon} />
    </button>
  );
}

/**
 * The arrows that move a row of the cicli up or down (2.55): only in the order
 * of the rows, off while the table is sorted by a column.
 */
export function MoveButtons({
  index,
  count,
  sorted,
  pending,
  onMove,
}: {
  index: number;
  count: number;
  sorted: boolean;
  pending: boolean;
  onMove: (direction: 'up' | 'down') => void;
}) {
  const hint = 'Togli l’ordinamento per colonna per spostare le righe';
  return (
    <>
      <RowAction
        icon="up"
        label={sorted ? hint : 'Sposta su'}
        disabled={sorted || pending || index === 0}
        onClick={() => onMove('up')}
      />
      <RowAction
        icon="down"
        label={sorted ? hint : 'Sposta giù'}
        disabled={sorted || pending || index === count - 1}
        onClick={() => onMove('down')}
      />
      <span className="row-actions-gap" aria-hidden />
    </>
  );
}
